// Inpatient billing (docs/plans/phase2-inpatient-billing.md §3.3): deposits, the stay's bill,
// interim and final bills, and packages.
import { queryOptions } from '@tanstack/react-query';
import {
  inpatientBillPrintedSchema,
  inpatientBillViewSchema,
  issueInpatientBillResultSchema,
  packageListSchema,
  packageViewSchema,
  stayBalanceSchema,
  stayBillingSchema,
  stayListSchema,
  takeDepositResultSchema,
  visitStayBillingSchema,
  type createPackageBodySchema,
  type DepositTender,
  type updatePackageBodySchema,
} from '@wodi/shared';
import type { z } from 'zod';

import { api } from './client.ts';

type In<T extends z.ZodType> = z.input<T>;

export const stayBillingKeys = {
  all: ['stay-billing'] as const,
  stay: (admissionId: string) => ['stay-billing', 'stay', admissionId] as const,
  visit: (visitId: string) => ['stay-billing', 'visit', visitId] as const,
  stays: ['stay-billing', 'stays'] as const,
  bill: (billId: string) => ['stay-billing', 'bill', billId] as const,
  packages: ['stay-billing', 'packages'] as const,
  adminPackages: ['admin', 'packages'] as const,
};

export const stayBillingQuery = (admissionId: string) =>
  queryOptions({
    queryKey: stayBillingKeys.stay(admissionId),
    queryFn: () => api('GET', `/admissions/${admissionId}/billing`, { schema: stayBillingSchema }),
  });

/** The stay a visit belongs to, or `{ stay: null }` for any other visit (the cashier). */
export const visitStayBillingQuery = (visitId: string) =>
  queryOptions({
    queryKey: stayBillingKeys.visit(visitId),
    queryFn: () =>
      api('GET', `/visits/${visitId}/stay-billing`, { schema: visitStayBillingSchema }),
  });

export const staysQuery = queryOptions({
  queryKey: stayBillingKeys.stays,
  queryFn: () => api('GET', '/inpatient-billing/stays', { schema: stayListSchema }),
});

export const inpatientBillQuery = (billId: string) =>
  queryOptions({
    queryKey: stayBillingKeys.bill(billId),
    queryFn: () => api('GET', `/inpatient-bills/${billId}`, { schema: inpatientBillViewSchema }),
  });

export const packagesQuery = queryOptions({
  queryKey: stayBillingKeys.packages,
  queryFn: () => api('GET', '/packages', { schema: packageListSchema }),
});

export const adminPackagesQuery = queryOptions({
  queryKey: stayBillingKeys.adminPackages,
  queryFn: () => api('GET', '/admin/packages', { schema: packageListSchema }),
});

export const takeDeposit = (
  admissionId: string,
  body: { clientRequestId: string; tender: DepositTender },
) => api('POST', `/admissions/${admissionId}/deposits`, { body, schema: takeDepositResultSchema });

export const issueInterimBill = (admissionId: string) =>
  api('POST', `/admissions/${admissionId}/interim-bills`, {
    schema: issueInpatientBillResultSchema,
  });

export const issueFinalBill = (admissionId: string) =>
  api('POST', `/admissions/${admissionId}/final-bill`, {
    schema: issueInpatientBillResultSchema,
  });

export const markInpatientBillPrinted = (billId: string) =>
  api('POST', `/inpatient-bills/${billId}/printed`, { schema: inpatientBillPrintedSchema });

export const applyPackage = (admissionId: string, packageId: string, clientRequestId: string) =>
  api('POST', `/admissions/${admissionId}/packages`, {
    body: { packageId, clientRequestId },
    schema: stayBillingSchema,
  });

export const removeStayPackage = (stayPackageId: string, reason: string) =>
  api('POST', `/admission-packages/${stayPackageId}/remove`, {
    body: { reason },
    schema: stayBillingSchema,
  });

export const createPackage = (body: In<typeof createPackageBodySchema>) =>
  api('POST', '/admin/packages', { body, schema: packageViewSchema });

export const updatePackage = (packageId: string, body: In<typeof updatePackageBodySchema>) =>
  api('PATCH', `/admin/packages/${packageId}`, { body, schema: packageViewSchema });

/** The balance only, which the discharge dialog shows (inpatient.read). */
export const stayBalanceQuery = (admissionId: string) =>
  queryOptions({
    queryKey: [...stayBillingKeys.stay(admissionId), 'balance'] as const,
    queryFn: () => api('GET', `/admissions/${admissionId}/balance`, { schema: stayBalanceSchema }),
  });
